import { useState, useMemo } from 'react';
import { Combobox, useCombobox, Pill, PillsInput } from '@mantine/core';
import { sortOptionsBySearchText } from '@/components/InputSelect/selectionUtils';
import {
  useMultiSelect,
  handleBackspaceRemoval,
} from '@/components/InputSelect/useMultiSelect';
import { renderMultiOption } from '@/components/InputSelect/MultiOption';
import TagManager from 'react-gtm-module';
import { useAppSelector } from '@/app/hooks';
import {
  getGoogleEventForAnalysis,
  GoogleEvents,
} from '@/features/googleEvents';
import type { FilterOption } from './FilterSelect';
import styles from './FilterInputSelect.module.css';
import { ButtonComponent } from '@/components/Button/Button';

interface FilterInputSelectProps {
  /**
   * Placeholder when there is no selection
   */
  placeholder?: string;
  /**
   * Filter contents
   */
  options: FilterOption[];
  /**
   * Selected value/s
   */
  value?: FilterOption[];
  /**
   * ID of the element that triggers this filter input select to open
   */
  triggerButtonId?: string;
  /**
   * Filter select onSubmit action
   */
  onSubmit?: (selected: FilterOption[]) => void;
  /**
   * Allow shift-click to select ranges
   */
  allowShiftSelect?: boolean;
}

export const FilterInputSelect = ({
  value,
  onSubmit = () => {},
  allowShiftSelect = false,
  ...props
}: FilterInputSelectProps) => {
  const combobox = useCombobox();
  const [optionSelected, setOptionSelected] = useState<FilterOption[]>(
    value ?? []
  );
  const [previousValue, setPreviousValue] = useState(value);
  const [inputValue, setInputValue] = useState('');
  const { selectedAnalysis } = useAppSelector((s) => s.analysisMap);
  const { captureShiftKey, handleClick } = useMultiSelect<FilterOption>({
    allowShiftSelect,
    getKey: (item) => item.value,
  });

  // Sync local selection when the controlled `value` prop changes (e.g., parent
  // resets it)
  if (value !== previousValue) {
    setPreviousValue(value);
    setOptionSelected(value ?? []);
  }

  const sortedOptions = useMemo(
    () => sortOptionsBySearchText(props.options, inputValue),
    [props.options, inputValue]
  );

  const visibleOptions = useMemo(() => {
    if (!inputValue) return sortedOptions;
    const lower = inputValue.toLowerCase();
    return sortedOptions.filter((opt) =>
      opt.label.toLowerCase().includes(lower)
    );
  }, [sortedOptions, inputValue]);

  const isSelected = (opt: FilterOption) =>
    optionSelected.some((s) => s.value === opt.value);

  const removeOption = (val: string) => {
    setOptionSelected((prev) => prev.filter((s) => s.value !== val));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) =>
    handleBackspaceRemoval(e, inputValue, optionSelected, removeOption);

  const handleOptionSubmit = (val: string) => {
    const selection = handleClick({
      val,
      visibleOptions,
      currentSelection: optionSelected,
    });
    if (!selection) return;

    if (selection.length) {
      TagManager.dataLayer({
        dataLayer: {
          event: getGoogleEventForAnalysis(
            selectedAnalysis,
            GoogleEvents.filter
          ),
        },
      });
    }

    setOptionSelected(selection);
  };

  return (
    <div className={styles.root} id="filters-container" tabIndex={0}>
      <div className={styles.wrapper} data-test-role="dropdown">
        <Combobox
          store={combobox}
          onOptionSubmit={handleOptionSubmit}
          keepMounted={false}
        >
          <Combobox.DropdownTarget>
            <PillsInput classNames={{ input: styles.input }}>
              <Pill.Group
                gap="var(--mantine-spacing-xxs)"
                className={styles.pillGroup}
              >
                {optionSelected.map((opt) => (
                  <Pill
                    size="md"
                    key={opt.value}
                    withRemoveButton
                    data-testid={`filter-pill-${opt.value}`}
                    onRemove={() => removeOption(opt.value)}
                    classNames={{
                      root: styles.pill,
                      remove: styles.pillRemove,
                    }}
                  >
                    {opt.label}
                  </Pill>
                ))}
                <Combobox.EventsTarget>
                  <PillsInput.Field
                    role="combobox"
                    className={styles.field}
                    placeholder={
                      optionSelected.length === 0 ? props.placeholder : ''
                    }
                    value={inputValue}
                    onChange={(e) => setInputValue(e.currentTarget.value)}
                    onKeyDown={handleKeyDown}
                    autoFocus
                  />
                </Combobox.EventsTarget>
              </Pill.Group>
            </PillsInput>
          </Combobox.DropdownTarget>
          <div className={styles.optionsContainer}>
            <Combobox.Options onMouseDownCapture={captureShiftKey}>
              {visibleOptions.map((opt) => (
                <Combobox.Option
                  value={opt.value}
                  key={opt.value}
                  data-testid={opt.value}
                  selected={isSelected(opt)}
                  className={styles.option}
                >
                  {renderMultiOption({
                    option: opt,
                    checked: isSelected(opt),
                  })}
                </Combobox.Option>
              ))}
            </Combobox.Options>
          </div>
        </Combobox>
      </div>
      <div className={styles.buttons}>
        <ButtonComponent
          label="Clear"
          variant="tertiary"
          data-testid="filter-input-select-clear"
          onClick={() => setOptionSelected([])}
        />
        <div
          className={styles.saveButton}
          data-testid="filter-input-select-save"
        >
          <ButtonComponent
            label="Save"
            onClick={() => onSubmit(optionSelected)}
          />
        </div>
      </div>
    </div>
  );
};
